import type { DealProductBuilderRow, RepoDealProductBuilderRow } from '@src/types/OTC/otcProductBuilderTypes';
import { repoFieldDefinitions } from '@src/utils/OTC/repoFieldDefinitions';
import { getColumnTitle, getColumnTooltip } from '@src/utils/fieldDefinitionUtils';
import type { useDisclosureWithContext } from '@talos/kyoko';
import { useMemo } from 'react';
import { useDealProductBuilderAssets } from '../DealProductBuilderAssetsProvider';
import type { DealProductBuilderColumn } from '../dealProductBuilderColDef';
import { DealProductBuilderGrid } from './DealProductBuilderGrid';
import { buildCurrencySelectorColumn } from './baseProductBuilderColumns';

interface RepoProductBuilderGridProps {
  panelID: string;
  productDetailsDisclosure: ReturnType<
    typeof useDisclosureWithContext<{ rowIndex: number; rowData: DealProductBuilderRow }>
  >;
}

export const RepoProductBuilderGrid = ({ panelID, productDetailsDisclosure }: RepoProductBuilderGridProps) => {
  const { availableBorrowedAssets, availableCollateralAssets } = useDealProductBuilderAssets();

  const customColumns = useMemo(() => {
    const repoAssetColumn = buildCurrencySelectorColumn<RepoDealProductBuilderRow>({
      key: 'collateralAsset',
      headerName: getColumnTitle(repoFieldDefinitions.repoAsset),
      currenciesToInclude: availableCollateralAssets,
      description: getColumnTooltip(repoFieldDefinitions.repoAsset) ?? '',
    });
    const borrowedAssetColumn = buildCurrencySelectorColumn<RepoDealProductBuilderRow>({
      key: 'baseAsset',
      headerName: getColumnTitle(repoFieldDefinitions.borrowedAsset),
      currenciesToInclude: availableBorrowedAssets,
      description: getColumnTooltip(repoFieldDefinitions.borrowedAsset) ?? '',
    });
    return [repoAssetColumn, borrowedAssetColumn] as DealProductBuilderColumn[];
  }, [availableBorrowedAssets, availableCollateralAssets]);

  return (
    <DealProductBuilderGrid
      panelID={panelID}
      productDetailsDisclosure={productDetailsDisclosure}
      customColumnDefs={customColumns}
      fieldDefinitions={repoFieldDefinitions}
    />
  );
};
